import React, { useEffect, useMemo, useRef, useState } from 'react';
import EventItem from './EventItem';
import { ParamsTimeType } from '@op/types/common';
import { sortTimeArrayListFunc } from '@op/utils';
interface DivData {
  eventLine: HTMLDivElement;
  eventBox: HTMLDivElement;
  width: number;
  height: number;
  left: number;
  show?: boolean;
  top?: number;
  bottom: number;
  maxHeight: number;
  direction: 'left' | 'right';
  isLast?: boolean;
}
/**
 * @deprecated 此组件已废弃，请使用 @op/components/ChartsEvents 替代
 * 新组件支持高优先级事件，且有更好的类型定义
 */
export default function EventWindows({
  myChart,
  eventList,
  format,
  maxTimeValue,
  showEventAxis,
  dateType,
  xAxisData,
  yAxisMin,
  eventDetailsNav,
}: {
  myChart: any;
  eventList: any;
  format: string;
  maxTimeValue: string;
  showEventAxis: boolean;
  dateType: ParamsTimeType;
  xAxisData: Array<string>;
  yAxisMin?: boolean;
  /** 当日事件数大于阈值时，点击气泡跳转详情（可传 shouldAllowDetailsNav 由业务侧算条数） */
  eventDetailsNav?: {
    minCountExclusive: number;
    onNavigate: (item: any, xAxisValue: string) => void;
    shouldAllowDetailsNav?: (xAxisValue: string) => boolean;
  };
}) {
  const [renderHide, setRenderHide] = useState(true);
  const halfBarWidthRef = useRef<number>(0);
  const renderPositionMap = useRef<any>({});
  function getBarInfo({ seriesName }: { seriesName: string }) {
    try {
      // 获取柱子的布局矩形
      const seriesModel = myChart.getModel().getSeriesByName(seriesName)[0];
      const layoutRect = seriesModel.getData().getItemLayout(0);
      // 计算柱子的宽度
      const barWidth = layoutRect?.width || 0;

      return {
        barWidth,
        halfBarWidth: barWidth / 2,
      };
    } catch (error) {
      return { barWidth: 0, halfBarWidth: 0 };
    }
  }

  /**
   * 放置板块逻辑
   *
   * 放置板块开始循环 top = 0 到 top = 最大高度[280] - 板块高度 ，寻找位置，尝试放置
   * 循环已放置的板块,并判断正在放置的板块和已放置板块是否重叠
   *
   * 发现与所有已放置的板块都重叠，设置show为false
   * 如果找到了一个不与已放置板块重叠的位置，则设置show为true
   * @param divs
   * @returns
   */
  function placeDivs(divs: DivData[]) {
    // 这里控制底部留下多少空白 ， 加油一个气泡高度dbo-event-box
    const maxHeight = 250;

    function countCurrentPosition(currentDiv: DivData, currentIndex: number) {
      currentDiv.top = 0; // 初始化 top 值
      let isPlaced = false;

      while (currentDiv.top <= maxHeight - currentDiv.height && !isPlaced) {
        isPlaced = true;
        for (let i = 0; i < currentIndex; i++) {
          const placedDiv = divs[i];
          if (placedDiv.show !== false) {
            // 检查是否重叠
            if (
              currentDiv.left < placedDiv.left + placedDiv.width &&
              currentDiv.left + currentDiv.width > placedDiv.left &&
              currentDiv.top < placedDiv.top + placedDiv.height &&
              currentDiv.top + currentDiv.height > placedDiv.top
            ) {
              isPlaced = false;
              break;
            }
          }
        }

        if (!isPlaced) {
          currentDiv.top += 1; // 尝试下一个位置
        } else {
          currentDiv.show = true;
        }
      }

      if (!isPlaced) {
        currentDiv.show = false;
      }
    }

    // 最后一个板块尝试减少高度进行强制放置
    divs.forEach((currentDiv, currentIndex) => {
      countCurrentPosition(currentDiv, currentIndex);

      if (currentIndex === divs.length - 1 && currentDiv.show === false) {
        while (currentDiv.height > 40) {
          currentDiv.height = currentDiv.height - 1;
          countCurrentPosition(currentDiv, currentIndex);
          // console.log(`Reducing height to ${currentDiv.height}`); // 添加日志

          if (currentDiv.show) {
            // 因为设置的是内容高度，所以减18   外部盒子和内部盒子相差18px
            currentDiv.height = currentDiv.height - 18;
            break;
          }
        }

        currentDiv.isLast = true;
      }
    });

    return divs;
  }

  /**
   * 批量设置事件气泡的布局和样式
   * 此函数根据提供的数据处理一组事件气泡（div元素），包括定位、显示和隐藏等操作
   * @param processedDivs 一个包含事件气泡相关数据的数组，用于批量设置
   */
  function batchSettingEventBubble(processedDivs: DivData[]) {
    // 遍历每个事件气泡数据项
    processedDivs.forEach(
      ({
        eventBox,
        eventLine,
        left,
        top,
        bottom,
        height,
        show,
        width,
        direction,
        maxHeight,
        isLast,
      }) => {
        // 如果事件气泡应该显示
        if (show) {
          // 设置事件气泡的位置
          eventBox.style.left = `${left}px`;
          eventBox.style.top = `${top}px`;
          if (isLast) {
            const eventBoxChildren: any =
              eventBox.querySelector('.dbo-event-box');
            if (eventBoxChildren) {
              eventBoxChildren.style.height = `${height}px`;
            }
          }

          // 尝试获取事件气泡中的线条元素
          // 如果线条元素存在，设置其样式
          // 计算线条的top和left位置，以及高度
          const lineTop = (top || 0) + height - 8;
          const lineLeft = direction === 'left' ? left : left + width - 2;
          const lineHeight = maxHeight - (top || 0) - height - bottom + 5;

          // 设置线条的样式
          eventLine.style.top = `${lineTop}px`;
          eventLine.style.left = `${lineLeft}px`;
          eventLine.style.height = `${lineHeight}px`;
        } else {
          // 如果事件气泡不应该显示，则将其隐藏
          eventBox.style.display = 'none';
          eventLine.style.display = 'none';
        }
      },
    );
  }

  /**
   * 获取事件板块的位置信息
   * 这里处理了边缘事件展示的逻辑，调整left值，并添加标识direction
   * @param eventBox
   * @param left
   * @param maxWidth
   * @param barWidth
   * @returns
   */
  function getEventItemInfo({
    eventBox,
    left,
    maxWidth,
    barWidth,
    barBottomPosition,
    maxHeight,
    eventLine,
  }: {
    eventBox: HTMLDivElement;
    left: number;
    maxWidth: number;
    barWidth: number;
    barBottomPosition: number;
    maxHeight: number;
    eventLine: HTMLDivElement;
  }): DivData {
    const { width, height } = eventBox.getBoundingClientRect();
    let realLeft = left;
    let direction: 'left' | 'right' = 'left';
    if (width + left > maxWidth) {
      realLeft = left + barWidth - width;
      direction = 'right';
    }

    return {
      width,
      height,
      bottom: maxHeight - barBottomPosition,
      maxHeight,
      left: realLeft,
      eventBox,
      eventLine,
      direction,
    };
  }

  useEffect(() => {
    const { halfBarWidth, barWidth } = getBarInfo({
      seriesName: 'TopTipsEvent',
    });
    halfBarWidthRef.current = halfBarWidth;
    const keys = Object.keys(renderPositionMap.current);

    const renderList = keys
      .map((key) => {
        const { barLeftPosition, eventBox, barBottomPosition, eventLine } =
          renderPositionMap.current[key] || {};
        if (!barLeftPosition || !eventBox) return;
        const left = barLeftPosition - halfBarWidth;
        return getEventItemInfo({
          eventBox,
          left,
          maxWidth: myChart.getWidth(),
          maxHeight: myChart.getHeight(),
          barWidth,
          barBottomPosition,
          eventLine,
        });
      })
      .filter((item) => item !== undefined);

    const processedDivs = placeDivs(renderList);

    batchSettingEventBubble(processedDivs);
  }, [renderHide, eventList]);

  const eventListRender = useMemo(() => {
    const list = Object.keys(eventList || {});
    list.sort(sortTimeArrayListFunc());
    return list.map((xAxisValue) => {
      const list = eventList[xAxisValue];
      return {
        key: xAxisValue,
        children: list,
      };
    });
  }, [eventList]);

  return (
    <>
      {eventListRender.map(({ key, children }, index) => {
        return (
          <EventItem
            myChart={myChart}
            xAxisValue={key}
            eventList={children}
            key={key}
            hide={renderHide}
            format={format}
            dateType={dateType}
            maxTimeValue={maxTimeValue}
            showEventAxis={showEventAxis}
            halfBarWidth={halfBarWidthRef.current}
            xAxisData={xAxisData}
            yAxisMin={yAxisMin}
            eventDetailsNav={eventDetailsNav}
            backPositionInfo={(item) => {
              renderPositionMap.current = {
                ...renderPositionMap.current,
                [index]: item,
              };

              if (
                Object.keys(renderPositionMap.current).length ===
                Object.keys(eventList || {}).length
              ) {
                setRenderHide(false);
              }
            }}
          ></EventItem>
        );
      })}
    </>
  );
}
